import { useState } from 'react'
import PropTypes from 'prop-types'
import { CurrencyDollarIcon, HandCoinsIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Alert } from '@/components/ui/alert'
import { Icon } from '@/components/ui/icon'

import ModalContainer from '../../ModalContainer/ModelContainer'
import TextField from '../../FormLegacy/FormFieldsV2/TextField'
import CheckboxField from '../../FormLegacy/FormFieldsV2/CheckboxField'
import Tooltip from '../../CreateEvent/components/Tooltip/Tooltip'

const PRICING_STEPS = Array(24)
  .fill(0)
  .map((_, index) => ({ from: index, to: index + 1, fee: 0, enabled: false }))

const transformPricing = (currentPricing) => {
  const newPricing = [...PRICING_STEPS].map((el) => ({ ...el, enabled: false }))
  for (const setup of currentPricing) {
    const index = setup.duration - 1
    newPricing[index].fee = setup.price / 100
    newPricing[index].enabled = true
  }
  return newPricing
}

const ServiceFeesModal = ({ initialPricing, onSave, onClose, shouldValidatePricing = false }) => {
  const { t } = useTranslation(['parking-dashboard', 'common'])
  const [pricing, setPricing] = useState(() => transformPricing(initialPricing))

  const [asyncProcessing, setAsyncProcessing] = useState(false)
  const [asyncError, setAsyncError] = useState('')
  const [validationError, setValidationError] = useState('')
  const [errorIndices, setErrorIndices] = useState([])

  const validatePricing = () => {
    const enabledPricing = pricing.filter((el) => el.enabled)
    const invalidIndices = []

    enabledPricing.forEach((item, index) => {
      if (index === 0) return
      const currentFee = parseFloat(item.fee) || 0
      const previousFee = parseFloat(enabledPricing[index - 1].fee) || 0
      if (currentFee <= previousFee) {
        invalidIndices.push(item.from)
      }
    })

    setErrorIndices(invalidIndices)
    return invalidIndices.length > 0 ? t('parking-dashboard:serviceFees.validationError') : null
  }

  const handleFieldChange = ({ target: { value } }, index) => {
    setValidationError('')
    setErrorIndices([])
    const newState = pricing.map((object, idx) => {
      if (idx === index) {
        return { ...object, fee: value }
      }
      return object
    })
    setPricing(newState)
  }

  const handleCheckboxChange = ({ target: { checked } }, index) => {
    setValidationError('')
    setErrorIndices([])
    const newState = pricing.map((object, idx) => {
      if (idx === index) {
        return { ...object, enabled: checked }
      }
      return object
    })
    setPricing(newState)
  }

  const renderStep = (from, to, fee, enabled, checkboxEnabled, showTooltip, isHeader, isError) => {
    return (
      <div className="service-fee-row" key={`${from}-${to}`}>
        <div className="hours-container">
          {isHeader
            ? t('parking-dashboard:serviceFees.hoursHeader')
            : t('parking-dashboard:serviceFees.hoursRange', { from, to })}
        </div>
        <div className="fee-container">
          {isHeader ? (
            t('parking-dashboard:serviceFees.feeHeader')
          ) : (
            <TextField
              onChange={(e) => handleFieldChange(e, from)}
              leftIcon={<Icon icon={CurrencyDollarIcon} size="2rem" />}
              disabled={!enabled}
              value={fee}
              name={`${from}-${to}`}
              style={{ width: '80px' }}
              isError={isError}
            />
          )}
        </div>
        <div className="checkbox-container">
          {isHeader
            ? null
            : checkboxEnabled && (
                <CheckboxField
                  name={`${from}-${to}-${enabled}`}
                  onChange={(e) => handleCheckboxChange(e, from)}
                  value={enabled}
                />
              )}
        </div>
        <div className="state-container">
          {isHeader
            ? ''
            : enabled
              ? t('parking-dashboard:serviceFees.on')
              : t('parking-dashboard:serviceFees.off')}
        </div>
        <div className="tooltip-container">
          {isHeader
            ? null
            : showTooltip && (
                <Tooltip
                  name={`${from}-${to}`}
                  tooltips={[t('parking-dashboard:serviceFees.tooltipText')]}
                />
              )}
        </div>
      </div>
    )
  }

  const renderHeader = () => renderStep(0, 0, 0, false, false, false, true)

  const updateValetSettings = async () => {
    if (shouldValidatePricing) {
      const error = validatePricing()
      if (error) {
        setValidationError(error)
        return
      }
      setValidationError('')
    }

    const newPricings = pricing
      .filter((el) => el.enabled)
      .map((el) => ({
        duration: el.to,
        price: el.fee * 100,
        enabled: el.enabled,
      }))

    const result = onSave(newPricings)

    if (result instanceof Promise) {
      setAsyncProcessing(true)
      setAsyncError('')

      try {
        await result
        onClose()
      } catch (error) {
        setAsyncError(error.message)
      }

      setAsyncProcessing(false)
    } else {
      onClose()
    }
  }

  return (
    <ModalContainer
      additionalClass="parking-dashboard-modal-pricing"
      onClose={onClose}
      headerTitle={t('parking-dashboard:serviceFees.title')}
      headerIcon={<Icon icon={HandCoinsIcon} size="2rem" />}
      closeable={!asyncProcessing}
    >
      <div className="service-fee-body">
        {asyncError && <Alert className="alert" type="error" title={asyncError} />}
        {validationError && <Alert className="alert" type="error" title={validationError} />}
        <div className="service-fee-list">
          {renderHeader()}
          {pricing.map(({ from, to, fee, enabled }) =>
            renderStep(
              from,
              to,
              fee,
              enabled,
              from !== 0 && from !== 23,
              from !== 0 && from !== 23,
              false,
              errorIndices.includes(from),
            ),
          )}
        </div>
      </div>
      <div className="service-fee-footer">
        <Button use="secondary" size="small" onClick={onClose} disabled={asyncProcessing}>
          {t('common:actions.cancel')}
        </Button>
        <Button size="small" onClick={updateValetSettings} loading={asyncProcessing}>
          {t('common:actions.save')}
        </Button>
      </div>
    </ModalContainer>
  )
}

ServiceFeesModal.propTypes = {
  initialPricing: PropTypes.array,
  onSave: PropTypes.func,
  onClose: PropTypes.func,
  shouldValidatePricing: PropTypes.bool,
}

export default ServiceFeesModal
